import React, { useState, Children } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, XIcon, AlertCircleIcon, TrashIcon } from 'lucide-react';
interface EditRoomFormProps {
  roomData: any;
  onSave: (roomData: any) => void;
  onCancel: () => void;
  onNavigateToAssets: () => void;
}
export function EditRoomForm({
  roomData: initialRoomData,
  onSave,
  onCancel,
  onNavigateToAssets
}: EditRoomFormProps) {
  const {
    t
  } = useTranslation('rooms');
  const [roomData, setRoomData] = useState(initialRoomData);
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('info');
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleRemoveAmenity = (amenity: string) => {
    setRoomData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((a: string) => a !== amenity)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-9903" data-mp="2pw2ixncsg2o8">
      <div className="flex items-center" data-id="element-9904" data-mp="2kwha8l7tgfyb">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-9905" data-mp="2kxiwzmy72qvv">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-9906" data-mp="o3ecl54ukz48" />
          <span data-id="element-9907" data-mp="j4ur15u61pce">{t('components.EditRoomForm.edit_room')}</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-9908" data-mp="rmmy6n4d7a9k">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-9909" data-mp="vngm3ght4p2g">
          <nav className="flex" role="tablist" aria-label="Room form sections" data-id="element-9910" data-mp="vm5knnbu3tei">
            <button role="tab" aria-selected={activeTab === 'info'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-9911" data-mp="10fgi4xuraxtr">
              {t('components.EditRoomForm.room_information')}
            </button>
            <button role="tab" aria-selected={activeTab === 'media'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={onNavigateToAssets} data-id="element-9912" data-mp="hmmc4tf42g1o">
              {t('components.EditRoomForm.media_assets')}
            </button>
          </nav>
        </div>
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6" data-id="element-9913" data-mp="321cfgb03li8h">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9914" data-mp="3g0v9jzw2qt1s">
            <div data-id="element-9915" data-mp="3b07n7risj0dw">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9916" data-mp="3azo447hem5ya">
                {t('components.AddRoomForm.room_name')}
                <span className="text-red-500" data-id="element-9917" data-mp="3kzontuj6qo30">*</span>
              </label>
              <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9918" data-mp="2knx31df1y32" />
            </div>
            <div data-id="element-9919" data-mp="7ks5e4467mi7">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9920" data-mp="7lbldfa9g0tl">
                {t('components.AddRoomForm.room_category')}
                <span className="text-red-500" data-id="element-9921" data-mp="w4dc88cl4frk">*</span>
              </label>
              <div className="relative" data-id="element-9922" data-mp="h8eadfg2jbuv">
                <div className="flex items-center" data-id="element-9923" data-mp="h9pb16e7w3hr">
                  <input type="text" value={roomData.category} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-9924" data-mp="m5whr1eftj0r" />

                  <button type="button" aria-label="Clear category" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  category: ''
                }))} data-id="element-9925" data-mp="d7c6umf4cdn3">
                    <XIcon size={16} aria-hidden="true" data-id="element-9926" data-mp="3pp90dubrp1rj" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-9927" data-mp="1rcxobom04ixe">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9928" data-mp="1rbw2qq7d0e1f">
              {t('components.AddRoomForm.room_code')}
            </label>
            <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9929" data-mp="1hdyfo618knae" />
          </div>
          <div className="mb-6" data-id="element-9930" data-mp="253ssinlk2bun">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9931" data-mp="213sev56a64on">
              {t('components.AddRoomForm.description')}
            </label>
            <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9932" data-mp="poveiz4nbt1z" />
          </div>
          <div className="flex flex-wrap gap-6 mb-6" data-id="element-9933" data-mp="3acwyumk2qj97">
            <div className="flex items-center" data-id="element-9934" data-mp="3abc5h09hx6cx">
              <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9935" data-mp="3f3wwju10p5cr" />

              <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-9936" data-mp="3t2mufbvnv926">
                {t('components.AddRoomForm.smoking')}
              </label>
            </div>
            <div className="flex items-center" data-id="element-9937" data-mp="1uhcccii30c1">
              <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9938" data-mp="1yq6qihqc870h" />

              <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-9939" data-mp="5dzl7aboh4jd">
                {t('components.AddRoomForm.accessibility')}
              </label>
            </div>
            <div className="flex items-center" data-id="element-9940" data-mp="e95valhze8rp">
              <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9941" data-mp="3mm4lis6mb57f" />

              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-9942" data-mp="3rlpqpkcds9sl">
                {t('components.AddRoomForm.status')}
              </label>
            </div>
          </div>
          {/* Room View and Size */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9943" data-mp="16v8j049gt4db">
            <div data-id="element-9944" data-mp="2ot3ra7jl39qj">
              <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9945" data-mp="2oslqqypw29ct">
                {t('components.AddRoomForm.room_view')}
                <span className="text-red-500" data-id="element-9946" data-mp="2fu07t2gi8sbi">*</span>
              </label>
              <div className="relative" data-id="element-9947" data-mp="2kr04imrgsipy">
                <div className="flex items-center" data-id="element-9948" data-mp="2ks1qvndxkrmz">
                  <input type="text" value={roomData.view} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-9949" data-mp="38guix8rfmfcb" />

                  <button type="button" aria-label="Clear view" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  view: ''
                }))} data-id="element-9950" data-mp="2yr1eqstmd2i3">
                    <XIcon size={16} aria-hidden="true" data-id="element-9951" data-mp="2korgkd1dtdek" />
                  </button>
                </div>
              </div>
            </div>
            <div data-id="element-9952" data-mp="2bnjuctc5430u">
              <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9953" data-mp="2bo1n3fzlfz40">
                {t('components.AddRoomForm.room_size_sq_m')}
              </label>
              <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9954" data-mp="21vvymg8jvztk" />
            </div>
          </div>
          {/* Room Amenities */}
          <div className="mb-6" data-id="element-9955" data-mp="1iy2xnjhhlzyt">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9956" data-mp="1iz4k0k3ye8vu">
              {t('components.AddRoomForm.room_amenities')}
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-9957" data-mp="14bnsfhfbcg3x">
              {roomData.amenities.map((amenity: string) => <div key={amenity} className="flex items-center bg-gray-100 rounded-md px-2 py-1" data-id="element-9958" data-mp="zdm7ss28o8qc">
                  <span className="text-sm" data-id="element-9959" data-mp="1dahjytdsegam">{amenity}</span>
                  <button type="button" aria-label={`Remove ${amenity}`} onClick={() => handleRemoveAmenity(amenity)} className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-9960" data-mp="19a7cfcacjhtn">
                    <XIcon size={12} aria-hidden="true" data-id="element-9961" data-mp="1x12zjw3uxouk" />
                  </button>
                </div>)}
            </div>
          </div>
          {/* Occupancy Settings */}
          <div className="mb-6" data-id="element-9962" data-mp="260ztod90nfx0">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-9963" data-mp="262awoaun0jn2">
              {t('components.AddRoomForm.occupancy_settings')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-9964" data-mp="210btp22t7oqn">
              <div data-id="element-9965" data-mp="yqlazf47u6tg">
                <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9966" data-mp="ymxf49bat8bk">
                  {t('components.AddRoomForm.base_adults_occupancy')}
                  <span className="text-red-500" data-id="element-9967" data-mp="1cgonvfh7go68">*</span>
                </label>
                <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9968" data-mp="1hfi6fmbcn5d9" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-9969" data-mp="3t52cbkr7nkpo">
                  {t('components.AddRoomForm.standard_number_of_allowed_adults_for')}
                </p>
              </div>
              <div data-id="element-9970" data-mp="3pnwqvfmk8j2s">
                <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9971" data-mp="3pofwpxyxrh0g">
                  {t('components.AddRoomForm.base_children_occupancy')}
                  <span className="text-red-500" data-id="element-9972" data-mp="3bvgmaae0gczr">*</span>
                </label>
                <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9973" data-mp="3btdmiyn235hu" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-9974" data-mp="3uoasaetnouru">
                  {t('components.AddRoomForm.standard_number_of_allowed_children_for')}
                </p>
              </div>
              <div data-id="element-9975" data-mp="axkzrjsnynuh">
                <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9976" data-mp="ax1qsskzr9sl">
                  {t('components.AddRoomForm.maximum_occupancy')}
                  <span className="text-red-500" data-id="element-9977" data-mp="1z9bhof9s257">*</span>
                </label>
                <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9978" data-mp="5ztbczr89jv1" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-9979" data-mp="plxygoj4ykso">
                  {t('components.AddRoomForm.maximum_number_of_guests_allowed')}
                </p>
              </div>
            </div>
            <div className="space-y-2 mb-4" data-id="element-9980" data-mp="3adhvw97n1mma">
              <div className="flex items-center" data-id="element-9981" data-mp="3ab58hgj42k1u">
                <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9982" data-mp="31vfzj6k7xykh" />

                <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-9983" data-mp="36thhtp9tltuw">
                  {t('components.AddRoomForm.allow_additional_adults')}
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-9984" data-mp="2swb1008nq58b">
                {t('components.AddRoomForm.allow_additional_adults_not_exceeding_total')}
              </p>
              <div className="flex items-center mt-2" data-id="element-9985" data-mp="20dxa6klqnjiq">
                <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9986" data-mp="1vgezops2ow8m" />

                <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-9987" data-mp="93lptzhkknny">
                  {t('components.AddRoomForm.allow_additional_children')}
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-9988" data-mp="3hjna9gq1pu42">
                {t('components.AddRoomForm.allow_additional_children_not_exceeding_total')}
              </p>
            </div>
            {/* Occupancy Summary */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-9989" data-mp="10u00zny77lvx">
              <div className="flex" data-id="element-9990" data-mp="10w2nhy056wvw">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-9991" data-mp="15st5wrzf64kh" />
                <div data-id="element-9992" data-mp="15uw25tr60txn">
                  <p className="text-sm text-red-700" data-id="element-9993" data-mp="1fwqa2mdudtip">
                    {t('components.AddRoomForm.this_room_has_a_base_occupancy')}{' '}
                    {roomData.baseAdultsOccupancy}{' '}
                    {t('components.AddRoomForm.adults_and')}{' '}
                    {roomData.baseChildrenOccupancy}{' '}
                    {t('components.EditRoomForm.children_with_a_total_maximum_occupancy')}{' '}
                    {roomData.maxOccupancy}.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-9994" data-mp="s6drmhkz0edv">
                    {t('components.AddRoomForm.additional_adults_are_not_allowed')}
                  </p>
                  <p className="text-sm text-red-700" data-id="element-9995" data-mp="j824wqn860qp">
                    {t('components.AddRoomForm.additional_children_are_not_allowed')}
                  </p>
                  <p className="text-sm text-red-700" data-id="element-9996" data-mp="2dl9uo3wcfm5h">
                    {t('components.EditRoomForm.additional_cost_per_child_or_adults')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Bed Configuration */}
          <div className="mb-6" data-id="element-9997" data-mp="2n1m5643tp0xz">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-9998" data-mp="2s2jm6dd3joxw">
              {t('components.AddRoomForm.bed_configuration')}
              <span className="text-red-500" data-id="element-9999" data-mp="2d4hj049z95h6">*</span>
            </h3>
            {roomData.bedConfigurations.map((config: any, index: number) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-10000" data-mp="2i299quyvyljs" data-mpi={index}>
                <div data-id="element-10001" data-mp="294ytswb4i88j">
                  <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10002" data-mp="294gzi580u439">
                    {t('components.AddRoomForm.bed_type')}
                  </label>
                  <div className="relative" data-id="element-10003" data-mp="hfzk6w494giy">
                    <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-10004" data-mp="hgr439ohkpte">
                      <option value="" data-id="element-10005" data-mp="105pbh98t2k64">
                        {t('components.AddRoomForm.select_bed_type')}
                      </option>
                      {bedTypes.map(type => <option key={type} value={type} data-id="element-10006" data-mp="196nk64u6vfv1">
                          {type}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-10007" data-mp="3ayiep7obd3q1">
                      <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-10008" data-mp="36y89vyc9cjcr">
                        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-10009" data-mp="2e95ccst9mpsu" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-10010" data-mp="1qecd47blgrhj">
                  <div className="flex-grow" data-id="element-10011" data-mp="1qfnbtsq6n91s">
                    <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10012" data-mp="24dkbixzxnvko">
                      {t('components.AddRoomForm.bed_count')}
                    </label>
                    <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-10013" data-mp="3bqvvv9tqlcsx" />
                  </div>
                  {roomData.bedConfigurations.length > 1 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-10014" data-mp="5uyqz8fi58jk">
                      <TrashIcon size={18} data-id="element-10015" data-mp="3sa1lcgakh21f" />
                    </button>}
                </div>
              </div>)}
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-10016" data-mp="otbddvvfcex3">
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-10017" data-mp="osjh0iu06d5r">
              {t('components.AddRoomForm.save')}
            </button>
          </div>
        </form>
      </div>
    </div>;
}